/**
 * Z-index scale.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Stacking values were being invented at each call site: 1, 50, 1000, 1250,
 * 1290, 1300, 9999, and 999999999. That last one is the tell -- when there is
 * no agreed ordering, the only way to win is to pick a bigger number, and the
 * next person has to pick a bigger one still.
 *
 * The values below deliberately sit in MUI's own scale, because the app renders
 * MUI Modals, Drawers and Tooltips that bring their own defaults:
 *
 *   MUI: appBar 1100, drawer 1200, modal 1300, snackbar 1400, tooltip 1500
 *
 * A concrete bug this prevents: the sidebar panel was set to 1300, which is
 * exactly MUI's Modal default. Equal values are resolved by DOM order, so the
 * sidebar and any dialog were one refactor away from swapping places -- and a
 * dialog rendered inline rather than through a portal would already lose.
 * SIDEBAR now sits at 1200 (drawer level), below modals, where it belongs.
 */
export const Z = {
  /** Raised content inside a card: badges, ribbons, absolute overlays. */
  RAISED: 1,
  /** Sticky elements within a scroll container (table headers, toolbars). */
  STICKY: 10,
  /** Controls floating over a map or canvas. */
  OVERLAY: 100,
  /** The sidebar backdrop on mobile. Must sit just under the drawer. */
  SIDEBAR_BACKDROP: 1190,
  /** The sidebar drawer/rail itself. MUI drawer level. */
  SIDEBAR: 1200,
  /** The floating hamburger that opens the drawer. Above content, below drawer. */
  SIDEBAR_TRIGGER: 1150,
  /** Dialogs and modals. Matches MUI's Modal default. */
  MODAL: 1300,
  /** Toasts. Above modals so an error is never hidden behind one. */
  SNACKBAR: 1400,
  /** Tooltips. Matches MUI. */
  TOOLTIP: 1500,
  /** Full-screen blocking loader. The only thing above tooltips. */
  LOADER: 1600,
} as const;

export type ZLayer = keyof typeof Z;
